import Section from "../components/Section";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";

import { ReactNode, useState, useEffect, useCallback } from "react";

import { ApplicationError } from "../general/Errors";
import { useModalContext } from "../components/ModalProvider";
import { createApiKey, listApiKeys, deleteApiKey, ApiKey } from "../UserAPI";
import { useGuideAction } from "../guide/useGuideAction";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { useUrlPageState } from "../utilities/dataTablePagination";

import type { ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, Check, Copy, Loader2, Plus, Trash2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";

/** Leerzustand der Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? (
        <div className="max-w-prose text-sm text-muted-foreground">{hint}</div>
      ) : null}
      {action}
    </div>
  );
}

/**
 * Radix erlaubt keinen leeren `value` in `SelectItem`. Der Bestandszustand
 * benutzt den leeren String für „unbegrenzt gültig“ — er bleibt unverändert,
 * nur die Auswahl transportiert ihn als Sentinel.
 */
const NEVER_EXPIRES = "never";

export default function ApiKeyManagement() {
  const { t } = useTranslation();
  const { pushError, showConfirmationDialog } = useModalContext() as any;
  const [_authentication] = useAtom(authenticationAtom) as any;
  const roleAccess = useRoleAccess();
  const apiKeysPagination = useUrlPageState("apiKeysPage");

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<string>("365");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const hasApiKeyAccess = roleAccess.canSeeApiKeys;

  const loadApiKeys = useCallback(async () => {
    if (!hasApiKeyAccess) return;

    try {
      setApiKeysLoading(true);
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error("Failed to load API keys:", error);
      pushError(error);
    } finally {
      setApiKeysLoading(false);
    }
  }, [hasApiKeyAccess, pushError]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  /**
   * Prefill from the URL (`?panel=agent&tab=apiKeys&guide=settings.apiKeys.create&name=…`):
   * the dialog is open and the name is filled in; the key is only created on click.
   *
   * The expiry deliberately stays at its default: `expiresInDays` is a select
   * with exactly four values; an outside value would show an empty select button
   * and would be sent to the API as `NaN`.
   */
  useGuideAction(
    "settings.apiKeys.create",
    (values) => {
      setNewKeyName(values.name ?? "");
      setNewlyCreatedKey(null);
      setKeyCopied(false);
      setCreateKeyDialogOpen(true);
    },
    { panel: "agent", tab: "apiKeys" }
  );

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      setCreatingKey(true);
      const result = await createApiKey(
        newKeyName.trim(),
        expiresInDays ? parseInt(expiresInDays) : undefined
      );
      setNewlyCreatedKey(result.accessToken);
      toast.success(t("apiPanel.keyCreatedSuccess"));
      await loadApiKeys();
    } catch (error) {
      if (error instanceof ApplicationError) {
        pushError(new Error(t(error.message)));
      } else {
        pushError(error);
      }
      setCreateKeyDialogOpen(false);
    } finally {
      setCreatingKey(false);
    }
  };

  const handleDeleteKey = async (keyId: string, keyName: string) => {
    showConfirmationDialog(
      t("apiPanel.deleteKeyTitle"),
      t("apiPanel.deleteKeyConfirm", { keyName }),
      async () => {
        try {
          await deleteApiKey(keyId);
          toast.success(t("apiPanel.keyDeletedSuccess"));
          await loadApiKeys();
        } catch (error) {
          pushError(error);
        }
      }
    );
  };

  const handleCopyKey = async () => {
    if (newlyCreatedKey) {
      await navigator.clipboard.writeText(newlyCreatedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateKeyDialogOpen(false);
    setNewKeyName("");
    setExpiresInDays("365");
    setNewlyCreatedKey(null);
    setKeyCopied(false);
  };

  const expirationOptions = [
    { key: "30", text: t("apiPanel.expires30Days") },
    { key: "90", text: t("apiPanel.expires90Days") },
    { key: "365", text: t("apiPanel.expires1Year") },
    { key: NEVER_EXPIRES, text: t("apiPanel.neverExpires") },
  ];

  const apiKeyColumns: ColumnDef<ApiKey>[] = [
    {
      id: "name",
      size: 250,
      header: () => t("apiPanel.keyName"),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">{row.original.name}</div>
      ),
    },
    {
      id: "createdAt",
      size: 150,
      header: () => t("apiPanel.created"),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: "expiresAt",
      size: 150,
      header: () => t("apiPanel.expires"),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.expiresAt
            ? new Date(row.original.expiresAt).toLocaleDateString()
            : t("apiPanel.never")}
        </div>
      ),
    },
    {
      id: "lastUsedAt",
      size: 150,
      header: () => t("apiPanel.lastUsed"),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.lastUsedAt
            ? new Date(row.original.lastUsedAt).toLocaleDateString()
            : t("apiPanel.neverUsed")}
        </div>
      ),
    },
    {
      id: "actions",
      size: 100,
      header: () => <span className="sr-only">{t("apiPanel.delete")}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            aria-label={t("apiPanel.deleteKeyAria", { keyName: row.original.name })}
            title={t("apiPanel.delete")}
            onClick={() => handleDeleteKey(row.original.id, row.original.name)}
          >
            <Trash2 aria-hidden className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (!hasApiKeyAccess) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {t("apiPanel.noPermission")}
      </div>
    );
  }

  return (
    <>
      <Section title={t("apiPanel.apiKeys")}>
        <div className="flex min-w-0 flex-col gap-4">
          <p className="max-w-prose text-sm text-muted-foreground">
            {t("apiPanel.apiKeysDescription")}
          </p>
          <div className="flex flex-wrap">
            <Button
              data-guide-id="settings.apiKeys.create"
              variant="outline"
              className="gap-2 max-sm:w-full"
              onClick={() => setCreateKeyDialogOpen(true)}
            >
              <Plus aria-hidden className="h-4 w-4" />
              {t("apiPanel.createKey")}
            </Button>
          </div>
          <div className="min-w-0 overflow-x-auto">
            <DataTable
              columns={apiKeyColumns}
              data={apiKeys}
              loading={apiKeysLoading}
              page={apiKeysPagination.page}
              onPageChange={apiKeysPagination.onPageChange}
              previousAriaLabel={t("global.back")}
              nextAriaLabel={t("global.next")}
              emptyMessage={
                <TableEmptyState
                  title={t("apiPanel.noApiKeysTitle")}
                  hint={t("apiPanel.noApiKeys")}
                  action={
                    <Button
                      data-guide-id="settings.apiKeys.create"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setCreateKeyDialogOpen(true)}
                    >
                      <Plus aria-hidden className="h-4 w-4" />
                      {t("apiPanel.createKey")}
                    </Button>
                  }
                />
              }
            />
          </div>
        </div>
      </Section>

      <Dialog
        open={createKeyDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseCreateDialog();
        }}
      >
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-[500px]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {newlyCreatedKey
                ? t("apiPanel.keyCreated")
                : t("apiPanel.createNewKey")}
            </DialogTitle>
          </DialogHeader>
          {newlyCreatedKey ? (
            <>
              <Alert variant="warning">
                <AlertTriangle aria-hidden className="h-4 w-4" />
                <AlertDescription>{t("apiPanel.keyWarning")}</AlertDescription>
              </Alert>
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="api-key-value">{t("apiPanel.yourApiKey")}</Label>
                <Textarea
                  id="api-key-value"
                  value={newlyCreatedKey}
                  readOnly
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>
              <DialogFooter className="gap-2 pt-4 sm:gap-0">
                <Button variant="outline" onClick={handleCloseCreateDialog}>
                  {t("global.close")}
                </Button>
                <Button className="gap-2" onClick={handleCopyKey}>
                  {keyCopied ? (
                    <Check aria-hidden className="h-4 w-4" />
                  ) : (
                    <Copy aria-hidden className="h-4 w-4" />
                  )}
                  {keyCopied ? t("apiPanel.copied") : t("apiPanel.copyKey")}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="flex min-w-0 flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="api-key-name">
                    {t("apiPanel.keyName")}
                    <span aria-hidden className="ml-0.5 text-destructive">
                      *
                    </span>
                  </Label>
                  <Input
                    id="api-key-name"
                    value={newKeyName}
                    placeholder={t("apiPanel.keyNamePlaceholder")}
                    onChange={(event) => setNewKeyName(event.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="api-key-expiration">
                    {t("apiPanel.expiration")}
                  </Label>
                  <Select
                    value={expiresInDays === "" ? NEVER_EXPIRES : expiresInDays}
                    onValueChange={(value) =>
                      setExpiresInDays(value === NEVER_EXPIRES ? "" : value)
                    }
                  >
                    <SelectTrigger id="api-key-expiration">
                      <SelectValue placeholder={t("global.pleaseSelect")} />
                    </SelectTrigger>
                    <SelectContent>
                      {expirationOptions.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="gap-2 pt-4 sm:gap-0">
                <Button
                  variant="outline"
                  disabled={creatingKey}
                  onClick={handleCloseCreateDialog}
                >
                  {t("global.cancel")}
                </Button>
                <Button
                  disabled={!newKeyName.trim() || creatingKey}
                  onClick={handleCreateKey}
                >
                  {creatingKey && (
                    <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {creatingKey ? t("apiPanel.creatingKey") : t("apiPanel.create")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
